import { useRef, type KeyboardEvent } from "react";
import styled from "styled-components";

import { Color, Flex, Icon, Text } from "src/elements";
import { useHoverClickable } from "src/utils/hoverClickable";

const Container = styled(Flex)<{
  width: string;
  useParent?: boolean;
  isCheckboxLeft?: boolean;
  isDisabled?: boolean;
  isSoftDisabled?: boolean;
}>`
  width: ${({ width }) => width};
  position: ${({ useParent }) => (useParent ? "static" : "relative")};
  flex-direction: ${({ isCheckboxLeft }) => (isCheckboxLeft ? "row-reverse" : "row")};
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  pointer-events: ${({ isDisabled, isSoftDisabled }) => (isDisabled || isSoftDisabled) && "none"};
`;

const ClickArea = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

export interface CheckboxProps {
  isChecked: boolean | "mixed";
  setIsChecked: (checked: boolean) => void;
  label?: string;
  isDisabled?: boolean;
  isSoftDisabled?: boolean;
  isCheckboxLeft?: boolean;
  isHeader?: boolean;
  width?: string;
  useParent?: boolean;
  name?: string;
  size?: "small" | "large";
  role?: "checkbox" | "menuitemcheckbox";
}

export const Checkbox = ({
  isChecked,
  setIsChecked,
  name = "checkbox",
  label,
  isDisabled,
  isSoftDisabled,
  isCheckboxLeft,
  isHeader,
  width = "100%",
  size,
  useParent,
  role,
}: CheckboxProps) => {
  const refClickArea = useRef<HTMLDivElement>(null);
  const { isHovered, isActive } = useHoverClickable(refClickArea);

  const checkboxColor =
    (isDisabled && Color.textDisabled) ||
    (isHovered && isActive && Color.primaryActive) ||
    Color.primary;

  const checkboxIcon =
    (isChecked === true && "checkboxOn") ||
    (isHovered && isActive && "checkboxOn") ||
    (isHovered && "checkboxHovered") ||
    (isChecked === "mixed" && "checkboxMixed") ||
    "checkboxOff";

  const textColor =
    (isDisabled && Color.textDisabled) || (isHeader && Color.primaryActive) || Color.textSecondary;

  const textVariant = (size === "small" && "caption1") || (isHeader && "body1") || "body2";
  const isInteractionDisabled = isDisabled || isSoftDisabled;

  const onClick = () => {
    if (isInteractionDisabled) return;

    switch (isChecked) {
      case false:
      case "mixed":
        setIsChecked(true);
        break;
      case true:
        setIsChecked(false);
        break;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isInteractionDisabled || (event.key !== " " && event.key !== "Enter")) return;

    event.preventDefault();
    onClick();
  };

  return (
    <Container
      useParent={useParent}
      width={width}
      isCheckboxLeft={isCheckboxLeft}
      isDisabled={isDisabled}
      isSoftDisabled={isSoftDisabled}
    >
      {label && (
        <Flex flexGrow={1}>
          <Text variant={textVariant} color={textColor}>
            {label}
          </Text>
        </Flex>
      )}

      <Flex minWidth="14px">
        <Icon name={checkboxIcon} color={checkboxColor} />
      </Flex>

      <ClickArea
        ref={refClickArea}
        isDisabled={isInteractionDisabled}
        role={role}
        tabIndex={role ? (isInteractionDisabled ? -1 : 0) : undefined}
        aria-checked={role ? (isChecked === "mixed" ? "mixed" : isChecked) : undefined}
        aria-disabled={role ? isInteractionDisabled : undefined}
        aria-label={role ? label || name : undefined}
        onClick={onClick}
        onKeyDown={role ? onKeyDown : undefined}
        data-testid={name}
      />
    </Container>
  );
};
